import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

export interface ChoiceChipOption {
  /** Stable identifier reported back through onChange; never rendered. */
  id: string;
  /** Visible label; also the chip's accessible name (never HTML). */
  label: string;
  /** Secondary hint shown next to the label (e.g. "archivada"). */
  hint?: string;
  /** Marks this single option as busy; taps on it are ignored while true. */
  pending?: boolean;
}

export interface ChoiceChipsProps {
  /** Accessible name for the whole chip group. */
  label: string;
  options: ChoiceChipOption[];
  /** Selected option ids; single mode holds zero or one id. */
  value: string[];
  mode: "single" | "multi";
  /** Called with a brand-new array; `value` itself is never mutated. Omitted in read-only mode. */
  onChange?: (next: string[]) => void;
  /** Multi mode only: once reached, unselected chips are disabled. */
  max?: number;
  /** Multi mode only: shown once `max` is reached. */
  maxHint?: string;
  /** Renders every chip as non-interactive, with no callback and out of the tab order. */
  readOnly?: boolean;
  className?: string;
}

const CHIP_BASE =
  "inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-full border px-3 py-2 text-base leading-none transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-default aria-disabled:opacity-50 md:text-[15px]";

/**
 * The 44px touch-target floor (guide §8) as an inline style, on top of the
 * `min-h-11`/`min-w-11` Tailwind classes above: a component test rendered in
 * isolation (no app-wide stylesheet in its module graph) never sees the
 * generated Tailwind CSS, so the inline style is what keeps the tap area
 * guaranteed — and testable — independently of whichever stylesheet happens
 * to be loaded around it.
 */
const CHIP_MIN_TAP_AREA: CSSProperties = { minHeight: 44, minWidth: 44 };

const CHIP_SELECTED = "border-primary bg-primary text-primary-foreground";

const CHIP_UNSELECTED =
  "border-input bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground";

/** Small check mark shown on a selected chip, next to (never instead of) the styling change. */
function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
    >
      <path
        d="M3 8.5L6.2 11.5L13 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Small spinner shown on a pending chip while its option is busy. */
function PendingIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      className="animate-spin"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeOpacity="0.25"
      />
      <path
        d="M14 8a6 6 0 0 0-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The quick-tap chip primitive (Almikia guide §6, vision #5 "opciones
 * rápidas"): a labelled group of options rendered as chips, single-select
 * (tapping the selected chip clears it) or multi-select (tapping toggles,
 * with an optional cap). Purely presentational and fully controlled — it
 * carries no data fetching, no strings of its own, and never mutates
 * `value`; every change is reported as a brand-new array via `onChange`.
 *
 * Selected state is never color-only: a check icon plus a filled style
 * (navy/primary, per guide §4) marks it, and it is exposed to assistive
 * tech via radio semantics in single mode or `aria-pressed` in multi mode.
 * A `readOnly` group renders plain, non-focusable spans (no button, no
 * callback), matching the read-only rendering the profile section (TASK-119)
 * needs for users who can see but not write a lead.
 */
export function ChoiceChips({
  label,
  options,
  value,
  mode,
  onChange,
  max,
  maxHint,
  readOnly = false,
  className,
}: ChoiceChipsProps) {
  const isMaxReached =
    mode === "multi" && typeof max === "number" && value.length >= max;

  function handleToggle(optionId: string, pending: boolean | undefined) {
    if (readOnly || pending) return;

    const isSelected = value.includes(optionId);

    if (mode === "single") {
      onChange?.(isSelected ? [] : [optionId]);
      return;
    }

    if (isSelected) {
      onChange?.(value.filter((id) => id !== optionId));
      return;
    }

    if (isMaxReached) return;
    onChange?.([...value, optionId]);
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        role={readOnly ? "group" : mode === "single" ? "radiogroup" : "group"}
        aria-label={label}
        className="flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const isSelected = value.includes(option.id);
          const content = (
            <>
              {option.pending ? (
                <PendingIcon />
              ) : isSelected ? (
                <CheckIcon />
              ) : null}
              <span>{option.label}</span>
              {option.hint ? (
                <span className="text-xs opacity-70">({option.hint})</span>
              ) : null}
            </>
          );

          if (readOnly) {
            return (
              <span
                key={option.id}
                data-testid="choice-chip"
                data-selected={isSelected}
                style={CHIP_MIN_TAP_AREA}
                className={cn(
                  CHIP_BASE,
                  isSelected ? CHIP_SELECTED : CHIP_UNSELECTED,
                )}
              >
                {content}
              </span>
            );
          }

          // A pending chip stays focusable (only `aria-disabled`, never native
          // `disabled`) so a keyboard user who just activated it doesn't lose
          // focus to <body> while the save is in flight; `handleToggle`
          // already ignores taps/Enter on it via its `pending` guard. Native
          // `disabled` is reserved for unselected chips blocked by `max`.
          const isCapped = !isSelected && isMaxReached;

          return (
            <button
              key={option.id}
              type="button"
              role={mode === "single" ? "radio" : undefined}
              aria-checked={mode === "single" ? isSelected : undefined}
              aria-pressed={mode === "multi" ? isSelected : undefined}
              aria-busy={option.pending || undefined}
              aria-disabled={option.pending || undefined}
              disabled={isCapped}
              onClick={() => handleToggle(option.id, option.pending)}
              data-testid="choice-chip"
              data-selected={isSelected}
              style={CHIP_MIN_TAP_AREA}
              className={cn(
                CHIP_BASE,
                isSelected ? CHIP_SELECTED : CHIP_UNSELECTED,
              )}
            >
              {content}
            </button>
          );
        })}
      </div>
      {!readOnly && isMaxReached && maxHint ? (
        <p
          data-testid="choice-chips-max-hint"
          className="text-xs text-muted-foreground"
        >
          {maxHint}
        </p>
      ) : null}
    </div>
  );
}
